"use client";

import { useEffect, useRef, useState } from "react";

// Per-character base cadence (ms). Deliberately unhurried — a constant
// fast tick reads as a machine ticker; this plus the jitter below reads
// as a person typing.
const BASE_CHAR_MS = 42;

/**
 * Humanized delay (ms) BEFORE revealing the next character, derived from
 * the char we just revealed (`prevChar`). Real typing isn't metronomic:
 * keystrokes vary, and people pause at the end of a sentence or clause.
 *   - ±40% jitter on every keystroke so no two land on the same beat
 *   - a long beat after sentence enders (`. ! ?`)
 *   - a shorter beat after clause punctuation (`, ; :`)
 */
function nextDelay(prevChar: string | undefined): number {
  const jitter = 0.6 + Math.random() * 0.8; // 0.6×–1.4×
  let ms = BASE_CHAR_MS * jitter;
  if (prevChar && ".!?".includes(prevChar)) ms += 380;
  else if (prevChar && ",;:".includes(prevChar)) ms += 180;
  return ms;
}

/**
 * Reveals `text` CHARACTER-by-character with a blinking block caret at the
 * head, like a live typing effect (the "Typewriter 01" look). The cadence
 * is humanized (jittered, with pauses after punctuation) rather than a
 * constant tick. The caret disappears once the full text is shown.
 *
 * When `startDelayMs > 0`, a brief "thinking" beat — three bouncing dots,
 * the same motif as the conversation's TypingBubble — plays first, so the
 * sender reads as composing before the words appear. Restarts (back to the
 * thinking beat) if `text` changes (e.g. a locale switch).
 *
 * Used for the operator's intro messages so they read as being composed
 * live rather than popping in whole.
 */
export function Typewriter({
  text,
  startDelayMs = 0,
  onDone,
}: {
  text: string;
  /** Hold a "typing…" loading indicator (bouncing dots) for this long
   *  before the first character appears — the sender "thinks" first. */
  startDelayMs?: number;
  onDone?: () => void;
}) {
  // -1 → still in the pre-type "thinking" beat (dots showing);
  //  0..length → that many characters revealed.
  const [shown, setShown] = useState(startDelayMs > 0 ? -1 : 0);
  // Keep the latest callback without re-arming the reveal timer.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // Restart the reveal whenever the source text changes — back to the
  // thinking beat so a locale switch re-types from scratch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resetting on `text` change is the intent
  useEffect(() => setShown(startDelayMs > 0 ? -1 : 0), [text]);

  useEffect(() => {
    // Thinking beat → reveal the first character after the hold.
    if (shown < 0) {
      const id = setTimeout(() => setShown(0), startDelayMs);
      return () => clearTimeout(id);
    }
    if (shown >= text.length) {
      onDoneRef.current?.();
      return;
    }
    const id = setTimeout(() => setShown((n) => n + 1), nextDelay(text[shown - 1]));
    return () => clearTimeout(id);
  }, [shown, text, startDelayMs]);

  // Pre-type "thinking" beat — three bouncing dots in the current text
  // colour, mirroring the TypingBubble indicator so the reveal reads as
  // the sender composing the message.
  if (shown < 0) {
    return (
      <span className="inline-flex items-end gap-1 align-middle" aria-hidden="true">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60" />
      </span>
    );
  }

  const done = shown >= text.length;
  return (
    <>
      {text.slice(0, shown)}
      {!done && (
        // Solid block caret — blinks while typing, gone when done.
        <span
          aria-hidden="true"
          className="ml-0.5 inline-block h-[1.05em] w-[0.5em] translate-y-[0.15em] animate-pulse rounded-[1px] bg-current align-baseline"
        />
      )}
    </>
  );
}
